import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 10: STHIR
 * Style: Zen Minimal — Ultra-minimal, calm, warm gray accents
 * Best for: Contemporary Indian fashion, Everyday wear, Curated essentials
 * Pure white theme with warm stone/gray palette. "Less is more" philosophy.
 */
const Template10 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Categories — text-only, no images
    const categories = [
        { name: 'Everyday', description: 'Effortless pieces for daily wear' },
        { name: 'Occasion', description: 'Subtle elegance for special moments' },
        { name: 'Heritage', description: 'Timeless Indian craft traditions' },
    ];

    // Products — Indian fashion with real Pexels images
    const products = [
        { id: 1, name: 'Handwoven Cotton Saree', price: '₹4,990', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Linen Kurta Set', price: '₹3,490', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Silk Blend Dupatta', price: '₹2,290', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Block Print Palazzo', price: '₹2,790', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Organic Khadi Shirt', price: '₹1,990', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Chanderi Anarkali', price: '₹5,490', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The simplicity is what drew me in. Every piece feels intentional, like it was made just for me. Nothing excess, nothing missing.',
            name: 'Meera Krishnan',
            city: 'Chennai',
        },
        {
            text: 'I have finally found a brand that understands that less truly is more. The fabrics are honest and the designs breathe.',
            name: 'Ananya Desai',
            city: 'Pune',
        },
        {
            text: 'Wearing STHIR feels like coming home. Calm, comfortable, and quietly beautiful. This is how fashion should be.',
            name: 'Rohan Gupta',
            city: 'Jaipur',
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) =>
                diff > 0
                    ? (prev + 1) % testimonials.length
                    : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    return (
        <div style={{ fontFamily: "'Inter', sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

            {/* ===== SECTION 1: TEXT HERO ===== */}
            {/* Mobile Hero */}
            <section className="md:hidden flex flex-col items-center justify-center min-h-[85vh] px-6 bg-white">
                <div className="text-center">
                    <h1 className="text-4xl font-light text-[#1C1917] leading-[1.1] mb-4 tracking-tight">
                        Less, But Better
                    </h1>
                    <div className="w-12 h-px bg-[#78716C] mx-auto mb-5" />
                    <p className="text-[#78716C] text-sm font-light leading-relaxed mb-8 max-w-[280px] mx-auto">
                        Curated Indian Fashion
                    </p>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="inline-flex items-center gap-3 text-[#78716C] text-xs tracking-[2px] uppercase font-normal hover:text-[#1C1917] transition-colors duration-300"
                    >
                        <span>Explore</span>
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                </div>
            </section>

            {/* Desktop Hero */}
            <section className="hidden md:flex min-h-[90vh] items-center justify-center px-8 bg-white">
                <div className="max-w-3xl mx-auto text-center">
                    <h1 className="text-5xl lg:text-7xl xl:text-8xl font-light text-[#1C1917] leading-[1.05] mb-6 tracking-tight">
                        Less, But Better
                    </h1>
                    <div className="w-16 h-px bg-[#78716C] mx-auto mb-6" />
                    <p className="text-[#78716C] text-base lg:text-lg font-light tracking-[3px] uppercase mb-12">
                        Curated Indian Fashion
                    </p>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="group inline-flex items-center gap-4 text-[#78716C] text-sm tracking-[2px] uppercase font-normal hover:text-[#1C1917] hover:gap-6 transition-all duration-300"
                    >
                        <span>Explore Collection</span>
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
            </section>

            {/* ===== SECTION 2: CATEGORIES — Text Links ===== */}
            {/* Mobile Categories */}
            <section className="md:hidden py-12 bg-white" style={{ borderTop: '1px solid #E7E5E4' }}>
                <div className="px-6">
                    <p className="text-[#A8A29E] text-[10px] tracking-[3px] uppercase mb-6 text-center">Shop By</p>
                    <div className="flex flex-col items-center gap-6">
                        {categories.map((cat, index) => (
                            <button
                                key={index}
                                onClick={() => navigate('/template10/shop')}
                                className="group text-center bg-transparent border-none cursor-pointer p-0"
                            >
                                <span className="text-[#1C1917] text-lg font-light tracking-wider group-hover:text-[#78716C] transition-colors duration-300">
                                    {cat.name}
                                </span>
                                <div className="w-0 group-hover:w-full h-px bg-[#78716C] mx-auto mt-1 transition-all duration-300" />
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Categories */}
            <section className="hidden md:block py-20 lg:py-24 bg-white" style={{ borderTop: '1px solid #E7E5E4' }}>
                <div className="max-w-[1200px] mx-auto px-8">
                    <p className="text-[#A8A29E] text-xs tracking-[3px] uppercase mb-12 text-center">Shop By</p>
                    <div className="flex justify-center items-start gap-16 lg:gap-24">
                        {categories.map((cat, index) => (
                            <button
                                key={index}
                                onClick={() => navigate('/template10/shop')}
                                className="group text-center bg-transparent border-none cursor-pointer p-0"
                            >
                                <span className="text-[#1C1917] text-xl lg:text-2xl font-light tracking-wider group-hover:text-[#78716C] transition-colors duration-300">
                                    {cat.name}
                                </span>
                                <div className="w-0 group-hover:w-full h-px bg-[#78716C] mx-auto mt-2 transition-all duration-300" />
                                <p className="text-[#A8A29E] text-xs font-light mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    {cat.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCT GRID ===== */}
            {/* Mobile Products */}
            <section className="md:hidden py-12 bg-[#FAFAF9]">
                <div className="px-5">
                    <p className="text-[#A8A29E] text-[10px] tracking-[3px] uppercase mb-2 text-center">The Edit</p>
                    <h2 className="text-xl font-light text-[#1C1917] mb-8 text-center">New Arrivals</h2>
                    <div className="grid grid-cols-2 gap-4">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template10/product/${product.id}`)}
                                className="group cursor-pointer"
                            >
                                <div className="aspect-[3/4] overflow-hidden bg-[#FAFAF9] mb-3">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <h3 className="text-[#1C1917] text-xs font-light line-clamp-1 mb-0.5">
                                    {product.name}
                                </h3>
                                <p className="text-[#78716C] text-xs font-light">{product.price}</p>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="w-full mt-8 py-3 text-[#78716C] text-xs tracking-[2px] uppercase font-normal bg-transparent cursor-pointer"
                        style={{ border: '1px solid #E7E5E4' }}
                    >
                        View All
                    </button>
                </div>
            </section>

            {/* Desktop Products */}
            <section className="hidden md:block py-20 lg:py-28 bg-[#FAFAF9]">
                <div className="max-w-[1200px] mx-auto px-8">
                    <div className="flex justify-between items-end mb-14">
                        <div>
                            <p className="text-[#A8A29E] text-xs tracking-[3px] uppercase mb-2">The Edit</p>
                            <h2 className="text-2xl lg:text-3xl font-light text-[#1C1917]">New Arrivals</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="group flex items-center gap-3 text-[#78716C] text-sm tracking-wider uppercase hover:text-[#1C1917] transition-colors duration-300 bg-transparent border-none cursor-pointer"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12 lg:gap-x-8 lg:gap-y-16">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template10/product/${product.id}`)}
                                className="group cursor-pointer"
                            >
                                <div className="aspect-[3/4] overflow-hidden bg-[#FAFAF9] mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <h3 className="text-[#1C1917] text-sm font-light mb-1 group-hover:underline underline-offset-4 decoration-[#78716C] transition-all duration-300">
                                    {product.name}
                                </h3>
                                <p className="text-[#78716C] text-sm font-light">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: TESTIMONIAL ===== */}
            {/* Mobile Testimonial */}
            <section className="md:hidden py-14 bg-white">
                <div className="px-6">
                    <div
                        className="overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div className="text-center">
                                        <p className="text-[#1C1917] text-base font-light leading-relaxed mb-6 italic">
                                            &ldquo;{testimonial.text}&rdquo;
                                        </p>
                                        <div className="w-8 h-px bg-[#E7E5E4] mx-auto mb-4" />
                                        <p className="text-[#1C1917] text-xs font-normal">{testimonial.name}</p>
                                        <p className="text-[#A8A29E] text-[11px] font-light">{testimonial.city}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-6">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className={`h-px rounded-none transition-all duration-300 border-none cursor-pointer ${
                                        activeTestimonial === i
                                            ? 'w-6 bg-[#78716C]'
                                            : 'w-3 bg-[#D6D3D1]'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Testimonial */}
            <section className="hidden md:block py-24 lg:py-32 bg-white">
                <div className="max-w-2xl mx-auto px-8 text-center">
                    <div
                        className="overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0">
                                    <div className="text-center">
                                        <p className="text-[#1C1917] text-xl lg:text-2xl font-light leading-relaxed mb-8 italic">
                                            &ldquo;{testimonial.text}&rdquo;
                                        </p>
                                        <div className="w-10 h-px bg-[#E7E5E4] mx-auto mb-4" />
                                        <p className="text-[#1C1917] text-sm font-normal">{testimonial.name}</p>
                                        <p className="text-[#A8A29E] text-xs font-light">{testimonial.city}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-10">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className={`h-px rounded-none transition-all duration-300 border-none cursor-pointer ${
                                        activeTestimonial === i
                                            ? 'w-8 bg-[#78716C]'
                                            : 'w-4 bg-[#D6D3D1]'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: NEWSLETTER ===== */}
            {/* Mobile Newsletter */}
            <section className="md:hidden py-14 bg-[#FAFAF9]" style={{ borderTop: '1px solid #E7E5E4' }}>
                <div className="px-6 text-center">
                    <p className="text-[#A8A29E] text-[10px] tracking-[3px] uppercase mb-3">Newsletter</p>
                    <h2 className="text-lg font-light text-[#1C1917] mb-2">Stay Quietly Informed</h2>
                    <p className="text-[#78716C] text-xs font-light mb-6">New arrivals. No noise.</p>
                    <div className="flex items-center max-w-sm mx-auto" style={{ borderBottom: '1px solid #78716C' }}>
                        <input
                            type="email"
                            placeholder="Your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 bg-transparent py-3 text-[#1C1917] text-sm font-light placeholder-[#A8A29E] focus:outline-none border-none"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                        />
                        <button
                            className="p-2 bg-transparent border-none cursor-pointer text-[#78716C] hover:text-[#1C1917] transition-colors duration-300"
                        >
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop Newsletter */}
            <section className="hidden md:block py-20 lg:py-28 bg-[#FAFAF9]" style={{ borderTop: '1px solid #E7E5E4' }}>
                <div className="max-w-lg mx-auto px-8 text-center">
                    <p className="text-[#A8A29E] text-xs tracking-[3px] uppercase mb-4">Newsletter</p>
                    <h2 className="text-2xl lg:text-3xl font-light text-[#1C1917] mb-3">
                        Stay Quietly Informed
                    </h2>
                    <p className="text-[#78716C] text-sm font-light mb-10">
                        New arrivals. No noise.
                    </p>
                    <div className="flex items-center max-w-md mx-auto" style={{ borderBottom: '1px solid #78716C' }}>
                        <input
                            type="email"
                            placeholder="Your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 bg-transparent py-3 text-[#1C1917] text-sm font-light placeholder-[#A8A29E] focus:outline-none border-none"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                        />
                        <button
                            className="p-2 bg-transparent border-none cursor-pointer text-[#78716C] hover:text-[#1C1917] transition-colors duration-300"
                        >
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template10;
